/**
 * Shared Leaflet map utilities for Manna Trust Operations Platform.
 * - Base-layer tile definitions (Streets, Satellite, Hybrid)
 * - Layer preference storage (per device, in-memory + localStorage)
 * - MapLayersControl: drop-in <LayersControl> for any MapContainer
 */
import React, { useEffect } from "react";
import { LayersControl, TileLayer, useMapEvents } from "react-leaflet";

// ─── TILE LAYER DEFINITIONS ──────────────────────────────────────────────────

export const TILE_LAYERS = {
  streets: {
    label: "Streets",
    url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
    attribution:
      '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    maxZoom: 19,
  },
  satellite: {
    label: "Satellite",
    url:
      "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
    attribution:
      "Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community",
    maxZoom: 19,
  },
  // Hybrid = Esri satellite imagery + CartoDB transparent labels on top
  hybrid_base: {
    url:
      "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
    attribution:
      "Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community",
    maxZoom: 19,
  },
  hybrid_labels: {
    url: "https://{s}.basemaps.cartocdn.com/light_only_labels/{z}/{x}/{y}{r}.png",
    attribution: '&copy; <a href="https://carto.com/">CARTO</a>',
    maxZoom: 19,
    subdomains: "abcd",
  },
};

// ─── LAYER PREFERENCE STORAGE ────────────────────────────────────────────────

const PREF_KEY = "manna_map_layer";

export function getLayerPref(defaultLayer = "streets") {
  try {
    // Map layer preference is UI-only, not a secret — sessionStorage is fine
    return sessionStorage.getItem(PREF_KEY) || localStorage.getItem(PREF_KEY) || defaultLayer;
  } catch {
    return defaultLayer;
  }
}

export function setLayerPref(layer) {
  try {
    // Prefer sessionStorage for per-tab persistence; fall back to localStorage for cross-session
    sessionStorage.setItem(PREF_KEY, layer);
    localStorage.setItem(PREF_KEY, layer);
  } catch (e) {
    // localStorage may be unavailable (private browsing, storage quota)
    if (process.env.NODE_ENV === "development") console.error("setLayerPref failed:", e);
  }
}

// ─── MAP LAYER CONTROL COMPONENT ─────────────────────────────────────────────

/**
 * Drop-in LayersControl for any MapContainer.
 * Place inside <MapContainer>:
 *   <MapLayersControl defaultLayer="hybrid" />
 *
 * Props:
 *   defaultLayer: "streets" | "satellite" | "hybrid"
 */
export function MapLayersControl({ defaultLayer = "streets" }) {
  const initialLayer = getLayerPref(defaultLayer);
  return (
    <LayersControl position="topright">
      {/* Streets */}
      <LayersControl.BaseLayer
        checked={initialLayer === "streets"}
        name="Streets"
      >
        <TileLayer
          url={TILE_LAYERS.streets.url}
          attribution={TILE_LAYERS.streets.attribution}
          maxZoom={TILE_LAYERS.streets.maxZoom}
        />
      </LayersControl.BaseLayer>

      {/* Satellite */}
      <LayersControl.BaseLayer
        checked={initialLayer === "satellite"}
        name="Satellite"
      >
        <TileLayer
          url={TILE_LAYERS.satellite.url}
          attribution={TILE_LAYERS.satellite.attribution}
          maxZoom={TILE_LAYERS.satellite.maxZoom}
        />
      </LayersControl.BaseLayer>

      {/* Hybrid — satellite base + transparent labels overlay */}
      <LayersControl.BaseLayer
        checked={initialLayer === "hybrid"}
        name="Hybrid"
      >
        <LayerGroup>
          <TileLayer
            url={TILE_LAYERS.hybrid_base.url}
            attribution={TILE_LAYERS.hybrid_base.attribution}
            maxZoom={TILE_LAYERS.hybrid_base.maxZoom}
          />
          <TileLayer
            url={TILE_LAYERS.hybrid_labels.url}
            attribution={TILE_LAYERS.hybrid_labels.attribution}
            maxZoom={TILE_LAYERS.hybrid_labels.maxZoom}
            subdomains={TILE_LAYERS.hybrid_labels.subdomains}
            pane="shadowPane"
          />
        </LayerGroup>
      </LayersControl.BaseLayer>
    </LayersControl>
  );
}

// ─── LAYER PREFERENCE SAVER ──────────────────────────────────────────────────

/** Listens for baselayerchange and persists the choice. Place inside MapContainer. */
export function LayerPreferenceSaver() {
  const map = useMapEvents({
    baselayerchange(e) {
      const key = e.name === "Streets"
        ? "streets"
        : e.name === "Satellite"
          ? "satellite"
          : "hybrid";
      setLayerPref(key);
    },
  });
  return null;
}

// ─── DRAGGABLE + CLICK-TO-PLACE MAP ──────────────────────────────────────────

/**
 * Invisible component that handles:
 *  - Click anywhere → move pin
 *  - Drag end → update pin
 * Place inside <MapContainer> alongside a <Marker ref={markerRef} draggable>.
 *
 * Props:
 *   onPositionChange: (lat, lng) => void
 */
export function ClickAndDragMap({ onPositionChange }) {
  useMapEvents({
    click(e) {
      onPositionChange(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
}

// ─── LAYERGROUP re-export (needed for Hybrid) ────────────────────────────────
import { LayerGroup } from "react-leaflet";
export { LayerGroup };
